import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import Swiper from 'swiper/bundle';

import { Accessibility, Cloudinary, Util, WindowReference, XmStore, XmStoreUtil } from 'core/services';
import { CmsCore } from 'store/cms/models';
import { MatDialog } from '@angular/material/dialog';
@Component({
    selector: 'xm-carousel',
    styleUrls: [
        './carousel.scss',
        './carousel-styles/a-spot-carousel.scss',
        './carousel-styles/network-review-carousel.scss',
        './carousel-styles/shop-carousel.scss',
        './carousel-styles/byod-carousel.scss',
        './carousel-styles/plan-benefits.scss',
        './carousel-styles/savings-calculator.scss',
        './carousel-styles/plan-carousel.scss',
        './carousel-styles/shop-pdp.scss',
        './carousel-styles/multi-line-pricing.scss'
    ],
    templateUrl: './carousel.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CarouselComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    private static ANIMATION_TIME: number = 250;

    @Input() public name: string;
    @Input() public description: string;
    @Input() public overrideButtonsAriaLabel: CmsCarousel;
    @Input() public slideClass: string;
    @Input() public upperSettings: XmSwiperOptions;
    @Input() public lowerSettings: XmSwiperOptions;
    @Input() public slideData: object[];
    @Input() public playIcon: MediaImageOptions;
    @Input() public pauseIcon: MediaImageOptions;
    @Input() public slideIndex: number = 0;
    @Output() public activeSlideIndex: EventEmitter<number> = new EventEmitter<number>();

    @Output() public voiceOverText: EventEmitter<CarouselVoiceOverParams> = new EventEmitter<CarouselVoiceOverParams>();

    public showLowerArrows: boolean = false;
    public showUpperArrows: boolean = false;
    public autoPlayStopped: boolean = true;
    public autoPlayBtnClicked: boolean = false;
    public carouselClass: string;
    public leftArrowIcon: MediaImageOptions;
    public rightArrowIcon: MediaImageOptions;
    public upperNextBtnClass: string = '';
    public upperPrevBtnClass: string = '';
    public allyLeftArrowText: string;
    public allyRightArrowText: string;
    public allyBulletText: string;
    public paginationClassName: string = 'swiper-pagination';
    public outsideArrows: boolean;

    public tabindex: number = -1;

    private slidesPerView: number;
    private accessibility: Accessibility;
    private upperCarousel: XmSwiper;
    private lowerCarousel: XmSwiper;
    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private windowRef: WindowReference;
    private dialog: MatDialog;

    constructor(xmStore: XmStore, accessibility: Accessibility, windowRef: WindowReference, dialog: MatDialog) {
        Object.assign(this, { xmStore, accessibility, windowRef, dialog });
    }

    public ngOnInit(): void {
        // set defaults
        this.carouselClass = this.slideClass || '';
        this.upperSettings = this.upperSettings || { id: 'default' };
        this.upperSettings.autoplay = this.upperSettings.autoplay || false;

        // Avoiding Bullets pagination issue if used more than once
        if (this.upperSettings.pagination && this.upperSettings.pagination !== true && this.upperSettings.pagination.el) {
            this.paginationClassName = this.upperSettings.pagination.el.toString().split('.').join('');
        }

        if (this.upperSettings.navigation && this.upperSettings.navigation !== true && this.upperSettings.navigation.nextEl && this.upperSettings.navigation.prevEl) {
            this.upperNextBtnClass = (<string> this.upperSettings.navigation.nextEl).substr(1);
            this.upperPrevBtnClass = (<string> this.upperSettings.navigation.prevEl).substr(1);

            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (cmsCore: CmsCore) => {
                this.leftArrowIcon = {
                    mobile: Cloudinary.generateMediaFromCms(cmsCore.icons.leftArrow)
                };

                this.rightArrowIcon = {
                    mobile: Cloudinary.generateMediaFromCms(cmsCore.icons.rightArrow)
                };

                this.allyLeftArrowText = cmsCore.carousel.leftArrowAriaLabel;
                this.allyRightArrowText = cmsCore.carousel.rightArrowAriaLabel;
                this.allyBulletText = cmsCore.carousel.bulletAriaLabel;

                if (this.overrideButtonsAriaLabel) {
                    this.allyLeftArrowText = this.overrideButtonsAriaLabel.leftArrowAriaLabel;
                    this.allyRightArrowText = this.overrideButtonsAriaLabel.rightArrowAriaLabel;
                }
            }));
        }

        this.showUpperArrows = this.upperSettings.showArrows;

        if (this.upperSettings.autoplay) {
            this.autoPlayStopped = false;
            this.tabindex = 0;

        }

        if (this.lowerSettings) {
            this.lowerSettings.slidesPerView = this.lowerSettings.slidesPerView === 'auto' || !this.lowerSettings.slidesPerView ? 1 : this.lowerSettings.slidesPerView;

            this.showLowerArrows = this.lowerSettings.showArrows && this.slideData.length > this.lowerSettings.slidesPerView;
        }

        this.subscriptions.push(this.windowRef.breakpoint.subscribe(this.breakpointSettingsCheck.bind(this)));
    }

    public updateAllyText(): void {
        this.accessibility.updateElementsAriaLabel(document.getElementsByClassName('swiper-pagination-bullet'), this.allyBulletText);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.slideData && !changes.slideData.firstChange) {
            this.reinitCarousels();
        }

        if (changes.upperSettings && !changes.upperSettings.firstChange) {
            const currentVisible: number = changes.upperSettings.currentValue.visibleSlide;
            const previousVisible: number = changes.upperSettings.previousValue.visibleSlide;
            if (currentVisible !== previousVisible) {
                this.upperSlideTo(currentVisible);
            }
        }

        if (changes.slideIndex && this.slideIndex >= 0) {
            this.upperSlideTo(this.slideIndex);
        }
    }

    public ngAfterViewInit(): void {
        this.createCarousels();
    }

    public allFocusIn(event: Event): void {
        if ((<HTMLInputElement> event.target).className !== 'toggle-button') {
            this.stopUpperAutoPlay();
        }
    }

    public holderMouseEnter(): void {
        this.stopUpperAutoPlay();
    }

    public holderMouseLeave(): void {
        if (!this.autoPlayBtnClicked) {
            this.startUpperAutoPlay();
        }
    }

    public paginationMouseEnter(): void {

        if (!this.autoPlayBtnClicked && this.upperSettings.autoplay) {
            this.stopUpperAutoPlay();
        }
    }

    public paginationMouseLeave(): void {
        if (!this.autoPlayBtnClicked) {
            this.startUpperAutoPlay();
        }
    }

    public upperCarouselFocusOut(): void {
        if (!this.autoPlayBtnClicked) {
            this.startUpperAutoPlay();
        }
    }

    public toggleAutoplayOnBtnClick(): void {
        if (this.upperCarousel.autoplay.running) {
            this.stopUpperAutoPlay();
            this.autoPlayBtnClicked = true;
        } else {
            this.startUpperAutoPlay();
            this.autoPlayBtnClicked = false;
        }
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    private breakpointSettingsCheck(currentBreakpoint: Breakpoint): void {
        let carouselSettingBreakpoint: number;

        if (this.upperSettings.breakpoints) {
            const settingsBreakpointsArr: number[] = Object.keys(this.upperSettings.breakpoints)
                // TODO: Due to tslint bug #2430, .map((key: string) => parseInt(key)) doesn't pass no-unnecessary-callback-wrapper.
                /* tslint:disable: no-unnecessary-callback-wrapper */
                .map((key: string) => parseInt(key))
                /* tslint:enable: no-unnecessary-callback-wrapper */
                .sort((a: number, b: number) => a - b);

            carouselSettingBreakpoint = settingsBreakpointsArr.find((breakpointKey: number) => breakpointKey >= currentBreakpoint.currentBreakpointMax);
        }

        if (carouselSettingBreakpoint) {
            this.setLocalSlidesPerView(this.upperSettings.breakpoints[carouselSettingBreakpoint].slidesPerView);
            this.toggleNavHidden(this.upperSettings.breakpoints[carouselSettingBreakpoint].navigation);
        } else {
            this.setLocalSlidesPerView(this.upperSettings.slidesPerView);
            this.toggleNavHidden(this.upperSettings.navigation);
        }

        if (this.upperCarousel) {
            this.accessibility.addRemoveSliderAttributes(this.upperSettings.id, this.upperCarousel.activeIndex, this.slidesPerView);
        }
    }

    private startUpperAutoPlay(): void {
        if (this.upperSettings.autoplay && !this.upperCarousel.autoplay.running && !this.dialog.openDialogs.length) {
            this.upperCarousel.autoplay.start();
            this.autoPlayStopped = false;
        }
    }

    private stopUpperAutoPlay(): void {
        if (this.upperSettings.autoplay && this.upperCarousel.autoplay.running) {
            this.upperCarousel.autoplay.stop();
            this.autoPlayStopped = true;
        }
    }

    private upperSlideTo(slideNumber: number): void {
        if (this.upperCarousel) {
            setTimeout(() => {
                if (this.upperSettings.loop) {
                    this.upperCarousel.slideToLoop(slideNumber, CarouselComponent.ANIMATION_TIME, false);
                } else {
                    this.upperCarousel.slideTo(slideNumber, CarouselComponent.ANIMATION_TIME, false);
                }
            });
        }
    }

    private reinitCarousels(): void {
        this.upperCarousel.destroy(true, true);

        if (this.lowerSettings) {
            this.lowerCarousel.destroy(true, true);
        }

        this.createCarousels();
    }

    private createCarousels(): void {
        if (!this.upperSettings) {
            return;
        }

        this.upperSettings = {
            ...this.upperSettings,
            a11y: { enabled: false },
            preventClicks: false,
            preventClicksPropagation: false,
            on: {
                slideChange: this.slideChange.bind(this),
                transitionEnd: this.upperSlideChangeEnd.bind(this)
            }
        };

        if (this.lowerSettings) {
            this.lowerSettings = {
                ...this.lowerSettings,
                a11y: { enabled: false },
                preventClicks: false,
                preventClicksPropagation: false,
                watchSlidesProgress: true,
                navigation: {
                    prevEl: '.prev-lower-button',
                    nextEl: '.next-lower-button'
                }
            };
        }

        setTimeout(() => {
            if (this.lowerSettings) {
                this.lowerCarousel = new Swiper(`#${this.lowerSettings.id}`, this.lowerSettings);
                this.upperSettings.thumbs = {
                    swiper: this.lowerCarousel
                };
            }

            this.upperCarousel = new Swiper(`#${this.upperSettings.id}`, this.upperSettings);
            this.setLocalSlidesPerView(this.upperCarousel.params.slidesPerView);

            if (this.slideIndex >= 0) {
                this.upperSlideTo(this.slideIndex);
            }

            if (this.upperCarousel) {
                this.accessibility.addRemoveSliderAttributes(this.upperSettings.id, this.upperCarousel.activeIndex, this.slidesPerView);
            }

            if (this.allyBulletText) {
                this.updateAllyText();
            }
        });
    }

    private setLocalSlidesPerView(slidesPerViewParam: number | 'auto'): void {
        this.slidesPerView = Number(slidesPerViewParam === 'auto' ? 1 : slidesPerViewParam);
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */
    private toggleNavHidden(navigation: any): void {
        this.showUpperArrows = Boolean(!navigation) || Boolean(navigation.prevEl);
        this.outsideArrows = this.upperSettings.outsideArrows && this.showUpperArrows;
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */

    private slideChange(): void {
        if (this.upperCarousel && this.upperSettings) {
            if (this.upperSettings.loop) {
                const index: number = this.upperCarousel.isBeginning ? this.slideData.length - 1 : this.upperCarousel.isEnd ? 0 : this.upperCarousel.activeIndex - 1;
                this.activeSlideIndex.emit(index);
            } else {
                this.activeSlideIndex.emit(this.upperCarousel.activeIndex);
            }
            this.accessibility.addRemoveSliderAttributes(this.upperSettings.id, this.upperCarousel.activeIndex, this.slidesPerView);
        }
    }

    private upperSlideChangeEnd(): void {
        if (this.upperSettings && this.upperCarousel) {
            const voiceOverParams: CarouselVoiceOverParams = {
                activeIndex: this.upperCarousel.activeIndex + 1,
                slidesPerView: this.slidesPerView,
                totalSlides: this.slideData.length
            };

            this.voiceOverText.emit(voiceOverParams);
            this.accessibility.focusVisibleSlider(this.upperSettings.id, this.upperCarousel, !this.autoPlayStopped);
        }

        if (this.lowerCarousel) {
            this.lowerCarousel.slideTo(this.upperCarousel.activeIndex, CarouselComponent.ANIMATION_TIME, false);

            // set the active index to match the upper carousel index
            this.lowerCarousel.previousIndex = this.lowerCarousel.activeIndex;
            this.lowerCarousel.activeIndex = this.upperCarousel.activeIndex;
            this.lowerCarousel.update();
        }
    }
}
